import styled from 'styled-components'

export const FloatingButtonContainer = styled.div<{
  $x: number
  $y: number
  $isDragging: boolean
  $isChecklistOpen: boolean
  $isModalOpen: boolean
  $isMobile: boolean
}>`
  position: fixed;
  bottom: ${({ $y }) => $y}px;
  left: ${({ $x }) => $x}px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  cursor: ${({ $isDragging }) => ($isDragging ? 'grabbing' : 'move')};
  transition: ${({ $isChecklistOpen, $isDragging }) =>
    $isChecklistOpen && !$isDragging ? 'bottom 0.3s ease' : 'none'};
  opacity: ${({ $isModalOpen, $isMobile }) =>
    $isMobile && $isModalOpen ? 0 : $isModalOpen ? 0.3 : 1};
  pointer-events: ${({ $isModalOpen }) => ($isModalOpen ? 'none' : 'auto')};
  visibility: ${({ $isMobile, $isModalOpen }) =>
    $isMobile && $isModalOpen ? 'hidden' : 'visible'};

  @media (max-width: 768px) {
    cursor: default;
  }
`

export const FloatingButton = styled.button<{
  $isHovered: boolean
  $isVibrating: boolean
  $isDragging: boolean
  $cursorStyle: 'pointer' | 'default'
}>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  isolation: isolate;
  background: linear-gradient(86.09deg, #349c90 -21.93%, #45d0c1 100.01%);
  box-shadow: 0px 8px 10px 1px rgba(5, 59, 75, 0.06);
  border-radius: 60px;
  border: none;
  cursor: ${({ $isDragging, $cursorStyle }) =>
    $isDragging ? 'grabbing' : $cursorStyle};
  transition: all 0.2s ease;
  height: 44px;
  white-space: nowrap;

  ${({ $isVibrating }) =>
    $isVibrating &&
    `
    animation: vibrate 0.6s ease-in-out;
  `}

  @keyframes vibrate {
    0%,
    100% {
      transform: translate(0);
    }
    10%,
    30%,
    50%,
    70%,
    90% {
      transform: translate(-2px, -2px);
    }
    20%,
    40%,
    60%,
    80% {
      transform: translate(2px, 2px);
    }
  }

  ${({ $isHovered }) =>
    $isHovered &&
    `
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 60px;
      background: rgba(5, 59, 75, 0.2);
      pointer-events: none;
      z-index: 1;
    }
  `}

  &:hover {
    box-shadow: 0px 10px 14px 2px rgba(5, 59, 75, 0.1);
    transform: translateY(-2px);
  }

  &:active {
    transform: scale(0.98) translateY(1px);
  }

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    padding: 12px;
    border-radius: 50%;

    &::after {
      display: none;
    }
  }
`

export const ButtonIcon = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  object-fit: contain;
  position: relative;
  z-index: 2;
`

export const ButtonText = styled.span`
  font-family: 'Averta', sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 1.3;
  color: #ffffff;
  white-space: nowrap;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    display: none;
  }
`

export const Badge = styled.div`
  position: absolute;
  top: -4px;
  right: 0;
  background: #ff5267;
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Averta', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1;
  z-index: 3;

  @media (max-width: 768px) {
    width: 16px;
    height: 16px;
    font-size: 12px;
  }
`

export const Backdrop = styled.div<{
  $isOpen: boolean
}>`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(5, 59, 75, 0.6);
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
    transition: opacity 0.3s ease;
    z-index: 9998;
  }
`

export const ChecklistContainer = styled.div<{
  $isOpen: boolean
  $alignment: 'left' | 'right'
  $buttonWidth: number
  $checklistWidth: number
}>`
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transform: ${({ $isOpen, $alignment, $buttonWidth, $checklistWidth }) => {
    if (!$isOpen) return 'translateY(20px)'
    if ($alignment === 'right') {
      const offset = $buttonWidth - $checklistWidth
      return `translateX(${offset}px) translateY(0)`
    }
    return 'translateY(0)'
  }};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  transition: opacity 0.3s ease, transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: ${({ $isOpen }) => ($isOpen ? 'calc(100vh - 120px)' : '0')};
  overflow: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  width: 447px;
  max-width: calc(100vw - 32px);

  @media (max-width: 768px) {
    position: fixed;
    top: auto;
    left: 0;
    right: 0;
    bottom: ${({ $isOpen }) => ($isOpen ? '0' : '-100%')};
    max-height: ${({ $isOpen }) => ($isOpen ? '80vh' : '0')};
    height: auto;
    width: 100%;
    max-width: 100%;
    transform: none;
    border-radius: 0;
    transition: bottom 0.4s cubic-bezier(0.4, 0, 0.2, 1),
      max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
    padding: 0;
    margin: 0;
    box-sizing: border-box;
    overflow: visible;
    z-index: 9999;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    width: 400px;
    transform: ${({ $isOpen, $alignment, $buttonWidth, $checklistWidth }) => {
      if (!$isOpen) return 'translateY(20px)'
      if ($alignment === 'right') {
        const offset = $buttonWidth - $checklistWidth
        return `translateX(${offset}px) translateY(0)`
      }
      return 'translateY(0)'
    }};
  }
`
